import React, {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  useMemo,
  useCallback,
  useEffect,
  useState,
} from 'react';

import type { Beer } from 'utils/types/Beer';
import type {
  Cart,
  CartItem,
  CartContextType,
  CartActions,
} from 'utils/types/Cart';
import { CartStorage } from './cartStorage';

// Estado inicial do carrinho
const initialCart: Cart = {
  items: [],
  totalItems: 0,
  totalValue: 0,
  subscriberTotalValue: 0,
};

// Estado inicial completo (incluindo drawer)
interface CartState {
  cart: Cart;
  isOpen: boolean;
}

// Função para carregar estado inicial
const getInitialState = (): CartState => {
  // Migrar dados antigos se necessário
  CartStorage.migrateOldData();

  // Tentar carregar carrinho do localStorage
  const savedCart = CartStorage.loadCart();

  return {
    cart: savedCart || initialCart,
    isOpen: false,
  };
};

// Reducer para gerenciar o estado do carrinho
const cartReducer = (state: CartState, action: CartActions): CartState => {
  switch (action.type) {
    case 'ADD_ITEM': {
      if (!action.payload?.beer) return state;

      const { beer } = action.payload;
      const quantity = action.payload.quantity || 1;

      const existingItemIndex = state.cart.items.findIndex(
        item => item.beer.id === beer.id,
      );

      let newItems: CartItem[];

      if (existingItemIndex >= 0) {
        // Item já existe, atualiza quantidade
        newItems = state.cart.items.map((item, index) =>
          index === existingItemIndex
            ? { ...item, quantity: item.quantity + quantity }
            : item,
        );
      } else {
        // Novo item
        const newItem: CartItem = {
          id: `${beer.id}-${Date.now()}`,
          beer,
          quantity,
          addedAt: new Date(),
        };
        newItems = [...state.cart.items, newItem];
      }

      const totalItems = newItems.reduce((sum, item) => sum + item.quantity, 0);
      const totalValue =
        Math.round(
          newItems.reduce(
            (sum, item) => sum + item.beer.price * item.quantity,
            0,
          ) * 100,
        ) / 100;
      const subscriberTotalValue =
        Math.round(
          newItems.reduce(
            (sum, item) => sum + item.beer.subscriberPrice * item.quantity,
            0,
          ) * 100,
        ) / 100;

      return {
        ...state,
        cart: {
          items: newItems,
          totalItems,
          totalValue,
          subscriberTotalValue,
        },
      };
    }

    case 'REMOVE_ITEM': {
      if (!action.payload?.beerId) return state;

      const newItems = state.cart.items.filter(
        item => item.beer.id !== action.payload!.beerId,
      );

      const totalItems = newItems.reduce((sum, item) => sum + item.quantity, 0);
      const totalValue =
        Math.round(
          newItems.reduce(
            (sum, item) => sum + item.beer.price * item.quantity,
            0,
          ) * 100,
        ) / 100;
      const subscriberTotalValue =
        Math.round(
          newItems.reduce(
            (sum, item) => sum + item.beer.subscriberPrice * item.quantity,
            0,
          ) * 100,
        ) / 100;

      return {
        ...state,
        cart: {
          items: newItems,
          totalItems,
          totalValue,
          subscriberTotalValue,
        },
      };
    }

    case 'UPDATE_QUANTITY': {
      if (!action.payload?.beerId || !action.payload?.quantity) return state;

      const { beerId, quantity } = action.payload;

      if (quantity <= 0) {
        // Se quantidade é 0 ou negativa, remove o item
        return cartReducer(state, { type: 'REMOVE_ITEM', payload: { beerId } });
      }

      const newItems = state.cart.items.map(item =>
        item.beer.id === beerId ? { ...item, quantity } : item,
      );

      const totalItems = newItems.reduce((sum, item) => sum + item.quantity, 0);
      const totalValue =
        Math.round(
          newItems.reduce(
            (sum, item) => sum + item.beer.price * item.quantity,
            0,
          ) * 100,
        ) / 100;
      const subscriberTotalValue =
        Math.round(
          newItems.reduce(
            (sum, item) => sum + item.beer.subscriberPrice * item.quantity,
            0,
          ) * 100,
        ) / 100;

      return {
        ...state,
        cart: {
          items: newItems,
          totalItems,
          totalValue,
          subscriberTotalValue,
        },
      };
    }

    case 'CLEAR_CART': {
      return {
        ...state,
        cart: initialCart,
      };
    }

    case 'TOGGLE_CART': {
      return {
        ...state,
        isOpen: !state.isOpen,
      };
    }

    case 'OPEN_CART': {
      return {
        ...state,
        isOpen: true,
      };
    }

    case 'CLOSE_CART': {
      return {
        ...state,
        isOpen: false,
      };
    }

    default:
      return state;
  }
};

// Criação do contexto
const CartContext = createContext<CartContextType | undefined>(undefined);

// Provider do contexto
interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, getInitialState());
  const [storageInfo, setStorageInfo] = useState(CartStorage.getStorageInfo());

  // Memoized actions
  const addToCart = useCallback((beer: Beer, quantity = 1) => {
    dispatch({ type: 'ADD_ITEM', payload: { beer, quantity } });
  }, []);

  const removeFromCart = useCallback((beerId: string) => {
    dispatch({ type: 'REMOVE_ITEM', payload: { beerId } });
  }, []);

  const updateQuantity = useCallback((beerId: string, quantity: number) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { beerId, quantity } });
  }, []);

  const clearCart = useCallback(() => {
    dispatch({ type: 'CLEAR_CART' });
  }, []);

  const toggleCart = useCallback(() => {
    dispatch({ type: 'TOGGLE_CART' });
  }, []);

  const openCart = useCallback(() => {
    dispatch({ type: 'OPEN_CART' });
  }, []);

  const closeCart = useCallback(() => {
    dispatch({ type: 'CLOSE_CART' });
  }, []);

  // Funcionalidades de persistência
  const saveCartToStorage = useCallback(() => {
    CartStorage.saveCart(state.cart);
    setStorageInfo(CartStorage.getStorageInfo());
  }, [state.cart]);

  const clearCartStorage = useCallback(() => {
    CartStorage.clearCart();
    setStorageInfo(CartStorage.getStorageInfo());
  }, []);

  // Efeito para salvar automaticamente o carrinho sempre que mudar
  useEffect(() => {
    if (state.cart.items.length > 0) {
      CartStorage.saveCart(state.cart);
      setStorageInfo(CartStorage.getStorageInfo());
    } else {
      // Se carrinho está vazio, limpar do storage
      CartStorage.clearCart();
      setStorageInfo(CartStorage.getStorageInfo());
    }
  }, [state.cart]);

  // Validar carrinho periodicamente (opcional - a cada 5 minutos)
  useEffect(() => {
    const validateInterval = setInterval(
      () => {
        const savedCart = CartStorage.loadCart();
        if (savedCart && savedCart.items.length !== state.cart.items.length) {
          // console.log('[CartProvider] Carrinho revalidado, atualizando estado');
          // Poderia dispatch uma ação para atualizar o carrinho se necessário
        }
      },
      5 * 60 * 1000,
    ); // 5 minutos

    return () => clearInterval(validateInterval);
  }, [state.cart.items.length]);

  // Memoized context value
  const contextValue = useMemo(
    (): CartContextType => ({
      cart: state.cart,
      isOpen: state.isOpen,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      toggleCart,
      openCart,
      closeCart,
      // Funcionalidades de persistência
      saveCartToStorage,
      clearCartStorage,
      isStorageAvailable: CartStorage.isStorageAvailable(),
      storageInfo,
    }),
    [
      state.cart,
      state.isOpen,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      toggleCart,
      openCart,
      closeCart,
      saveCartToStorage,
      clearCartStorage,
      storageInfo,
    ],
  );

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
};

// Hook para usar o contexto
export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext;
